/**
 * Where the contact form actually posts, and how it knows the post arrived.
 *
 * 🔴 THE BUG THIS FIXES, WHICH WAS LIVE ON PRODUCTION.
 *
 * `ContactPage.tsx` posted to a RELATIVE `/api/contact`. This site is S3 +
 * CloudFront via `infrastructure/modules/static-site`, which has exactly one
 * ordered behaviour (`assets/*`) and NO `/api/*` route — and, for SPA routing,
 * maps both 403 and 404 to **200 `/index.html`**.
 *
 * So the request never left the CDN, and the sequence was:
 *
 *     fetch('/api/contact')  ->  200  (text/html, the SPA shell)
 *     response.ok            ->  true
 *     setStatus('success')   ->  "Message Received. We'll be in touch
 *                                 within 1-2 business days."
 *
 * Every enquiry to an investment company's contact form was discarded, and
 * every sender was told it had arrived. Verified against production 2026-08-17:
 *
 *     GET https://fairmeadow.capital/api/contact
 *       -> HTTP 200, content-type: text/html, body <!doctype html>
 *
 * The API was fine the whole time — `api.fairmeadow.capital` resolves, the
 * Lambda and API Gateway are deployed, and the preflight answers 204 with
 * `access-control-allow-origin: https://fairmeadow.capital`. The frontend
 * simply never pointed at it: `VITE_API_URL` was declared in `vite-env.d.ts`
 * and referenced nowhere in `src/`.
 *
 * ── WHY THE VAR IS REQUIRED AND HAS NO FALLBACK ────────────────────────────
 *
 * The first version of this file read
 *
 *     import.meta.env.VITE_API_URL || 'https://api.fairmeadow.capital'
 *
 * on the grounds that no buildspec passed the var, so a literal was the only
 * thing that could not be empty. `assert-vite-env.mjs`'s own docblock rules that
 * out, and names this construct specifically: finboard's `main.tsx` reads
 * `import.meta.env.VITE_API_URL || 'http://localhost:3001'`, "so the same
 * missing var would inline LOCALHOST into a production bundle."
 *
 * A correct-looking literal is the worse version of that bug, not the safer one.
 * It fails safe in prod and SILENTLY WRONG everywhere else — a dev, preview or
 * FSD-hosted build with the var unset would post real enquiries to the
 * production API and look completely healthy doing it. That is the same shape as
 * the outage this file exists to fix: right-looking behaviour, wrong target,
 * nothing to see.
 *
 * So the var is REQUIRED and this throws when it is missing. Three things then
 * have to be true together, and each catches the failure earlier than the last:
 *
 *   1. `cicd.tf` sets `VITE_API_URL` on the frontend CodeBuild project, from
 *      `local.api_domain` — the same local the API Gateway domain is built from,
 *      so the two cannot drift.
 *   2. `buildspec-frontend.yml` runs `assert-vite-env.mjs` before `vite build`,
 *      which fails the BUILD on a missing var rather than inlining a bad value.
 *   3. This throw, at module scope. The pipeline runs `npm run prerender`, which
 *      renders every route under Node — so an unset var throws there and the
 *      build fails. VERIFIED, because the obvious version of this claim is
 *      wrong: plain `npm run build` is `tsc -b && vite build`, which never
 *      executes the module and exits 0 with the bad bundle. It is the PRERENDER
 *      that catches it (`npm run prerender` -> exit 1, with the message below;
 *      exit 0 once the var is set). A site that stopped prerendering would lose
 *      this layer silently, which is why (2) is the one that must not be removed.
 *
 * The throw is at module scope on purpose. `contact-api` is imported only by
 * `ContactPage`, so the blast radius of a misconfiguration is the contact route
 * failing loudly, never the whole site quietly posting somewhere wrong.
 */

/**
 * Absolute origin of the contact API, e.g. `https://api.fairmeadow.capital`.
 *
 * @throws if `VITE_API_URL` is unset or empty at build time.
 */
function requireApiBase(): string {
  const raw = import.meta.env.VITE_API_URL?.trim()
  if (!raw) {
    throw new Error(
      'VITE_API_URL is unset. Vite inlines this at BUILD time, so an empty value ships a ' +
        'relative URL and the contact form silently posts into the CDN. Set it on the frontend ' +
        'CodeBuild project (terraform/prod/cicd.tf) — see scripts/ci/assert-vite-env.mjs.'
    )
  }
  // A relative or protocol-less value reintroduces the original bug, so it is
  // refused rather than concatenated into something that looks like a URL.
  if (!/^https?:\/\//.test(raw)) {
    throw new Error(`VITE_API_URL must be an absolute http(s) origin; got "${raw}".`)
  }
  return raw.replace(/\/+$/, '')
}

export const CONTACT_API_BASE: string = requireApiBase()

export interface ContactSubmission {
  name: string
  email: string
  message: string
  phone?: string
  investorType?: string
  /** The honeypot. Always sent; empty for a human, filled by a bot. */
  _hp?: string
  /** `g-recaptcha-response`, when the site key is configured. */
  captchaToken?: string
}

/**
 * Google's PUBLIC reCAPTCHA site key, or `''` when this build has no captcha.
 *
 * UNLIKE `VITE_API_URL` ABOVE, AN EMPTY VALUE IS ALLOWED AND IS NOT A BUG. That
 * var is required and throws, because a wrong API origin silently posts real
 * enquiries into the void. This one is different in both directions:
 *
 *   - empty is a legitimate state (a build made before the key was registered),
 *     and it degrades to honeypot-only rather than to a broken form;
 *   - if it is empty while the SERVER requires a token, the server returns 403
 *     and the sender is told plainly. The halves disagree loudly, never
 *     silently.
 *
 * Terraform derives this and the Lambda's `CONTACT_CAPTCHA_REQUIRED` from the
 * same secrets blob in one apply, so reaching that 403 state takes deliberate
 * effort rather than a forgotten step.
 */
export const RECAPTCHA_SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() ?? ''

/**
 * POST the form and confirm it reached the API.
 *
 * THROWS on any failure, and the check is deliberately stricter than
 * `response.ok`. `res.ok` was true throughout the outage — CloudFront's SPA
 * fallback answers 200 — so a status check alone cannot tell "the API accepted
 * it" from "something returned a web page". Requiring the API's own
 * `{ success: true }` envelope means anything that is not the API is a failure,
 * including a future CDN misroute that resurrects the original bug.
 */
/**
 * An error the API itself described, as opposed to one the network produced.
 *
 * The distinction is not cosmetic. "Verification failed. Please complete the
 * checkbox and try again." tells a visitor what to do; "Failed to fetch" and
 * "NetworkError when attempting to fetch resource" are browser strings that
 * mean nothing to an investor and look like the site is broken. Only the first
 * kind is safe to render, so the two are different types rather than a string
 * the caller has to guess about.
 */
export class ContactApiError extends Error {
  /** Safe to show a visitor: it came from our API's `error` field. */
  readonly userFacing: boolean
  readonly status: number

  constructor(message: string, opts: { userFacing: boolean; status: number }) {
    super(message)
    this.name = 'ContactApiError'
    this.userFacing = opts.userFacing
    this.status = opts.status
  }
}

export async function submitContactForm(data: ContactSubmission): Promise<string> {
  const res = await fetch(`${CONTACT_API_BASE}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })

  // `.catch(() => null)` because the failure mode being defended against
  // returns HTML — `res.json()` rejects on it, and that rejection IS the signal.
  const body = (await res.json().catch(() => null)) as { success?: boolean; message?: string; error?: string } | null

  if (!res.ok || body?.success !== true) {
    // `body.error` exists only when the API answered in its own envelope. A CDN
    // misroute or an HTML error page leaves it undefined, and that case must NOT
    // be shown to the visitor — it is the signature of the outage this file
    // exists to catch, and it needs the generic message plus a look at the logs.
    throw new ContactApiError(body?.error || `Contact API returned ${res.status}`, {
      userFacing: typeof body?.error === 'string' && body.error.length > 0,
      status: res.status,
    })
  }

  return body.message || 'Thank you for your message. We will be in touch soon.'
}
